import * as React from 'react'

type FormControlContextValue = {
  id: string
  labelId?: string
  describedBy?: string
}

export const FormControlContext =
  React.createContext<FormControlContextValue | null>(null)

export const mergeIds = (
  ...ids: Array<string | undefined>
): string | undefined => {
  const mergedIds = ids.flatMap((id) => id?.split(/\s+/) ?? []).filter(Boolean)
  return mergedIds.length ? Array.from(new Set(mergedIds)).join(' ') : undefined
}

/**
 * Merges Form descriptions and custom control labels at the interactive element.
 * Pass native: true for controls that use native htmlFor labels.
 * Outside Form.Control, the hook returns the supplied props unchanged.
 */
export const useFormControlProps = <
  T extends Pick<
    React.HTMLAttributes<HTMLElement>,
    'id' | 'aria-label' | 'aria-labelledby' | 'aria-describedby'
  >,
>(
  props: T,
  { native = false }: { native?: boolean } = {},
): T => {
  const context = React.useContext(FormControlContext)

  // Only the slotted control receives the field id. Nested inputs must not
  // inherit the field's labels and descriptions.
  if (!context || props.id !== context.id) return props

  return {
    ...props,
    'aria-describedby': mergeIds(
      props['aria-describedby'],
      context.describedBy,
    ),
    'aria-labelledby':
      props['aria-labelledby'] ??
      (native || props['aria-label'] !== undefined
        ? undefined
        : context.labelId),
  }
}
